import React, { useState, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Card from "@amzn/meridian/card";
import Toggle from "@amzn/meridian/toggle";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableHead,
  DataTableRow,
  DataTableCell,
} from "@amzn/meridian/data-table";
import Alert from "@amzn/meridian/alert";
import apiClient from "src/utils/apiClient";

interface PeriodMetricData {
  period_label: string;
  period_start: string;
  period_end: string;
  total_incidents: number;
  override_count: number;
  override_rate: number;
}

interface FieldPeriodMetricData extends PeriodMetricData {
  field_name: string;
}

interface DQOMetricsResponse {
  period_type: string;
  year: number;
  overall_metrics: PeriodMetricData[];
  field_metrics: FieldPeriodMetricData[];
}

interface DQOMetricsProps {
  periodType: string;
  year: number;
}

const DQOMetrics: React.FC<DQOMetricsProps> = ({ periodType: initialPeriodType, year }) => {
  const [metricsData, setMetricsData] = useState<DQOMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>(initialPeriodType);
  const [showCounts, setShowCounts] = useState(false);

  const fetchMetrics = async (periodType: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post("/audit_workflow/dqo_metrics", {
        period_type: periodType,
        year: year,
      });
      setMetricsData(response.data);
    } catch (err) {
      setError("Failed to load DQO metrics");
      console.error("Error fetching DQO metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics(selectedPeriodType);
  }, [selectedPeriodType, year]);

  const formatPercentage = (value: number) => `${value.toFixed(2)}%`;
  const formatNumber = (value: number) => value.toLocaleString();

  const MetricCard: React.FC<{ title: string; value: string | number; subtitle?: string }> = ({
    title,
    value,
    subtitle,
  }) => (
    <Card>
      <Column spacing="200" alignmentHorizontal="center">
        <Text type="h400" color="secondary">
          {title}
        </Text>
        <Text type="d100" color="primary">
          {value}
        </Text>
        {subtitle && (
          <Text type="b200" color="secondary">
            {subtitle}
          </Text>
        )}
      </Column>
    </Card>
  );

  if (loading) {
    return (
      <Row alignmentHorizontal="center" spacing="200">
        <Text>Loading DQO metrics...</Text>
      </Row>
    );
  }

  if (error) {
    return (
      <Alert type="error" title="Error loading metrics">
        {error}
      </Alert>
    );
  }

  if (!metricsData) {
    return <Text>No DQO metrics data available</Text>;
  }

  const totalAudits = metricsData.overall_metrics.reduce(
    (sum, period) => sum + period.total_incidents,
    0,
  );
  const totalDQO = metricsData.overall_metrics.reduce(
    (sum, period) => sum + period.override_count,
    0,
  );
  const overallDQORate = totalAudits > 0 ? (totalDQO / totalAudits) * 100 : 0;

  const fieldTotals = metricsData.field_metrics.reduce(
    (acc, field) => {
      if (!acc[field.field_name]) {
        acc[field.field_name] = { count: 0, incidents: 0 };
      }
      acc[field.field_name].count += field.override_count;
      acc[field.field_name].incidents += field.total_incidents;
      return acc;
    },
    {} as Record<string, { count: number; incidents: number }>,
  );

  return (
    <Column spacing="400">
      <Column spacing="300">
        <Text type="h200">{metricsData.year} Incident DQO Summary</Text>
        <Row spacing="300">
          <MetricCard title="Total Audits" value={formatNumber(totalAudits)} />
          <MetricCard title="Total DQO Rate" value={formatPercentage(overallDQORate)} />
        </Row>
      </Column>

      <Column spacing="300">
        <Row widths={["50%", "50%"]} alignmentVertical="center">
          <Text type="h200">Period Breakdown with Field Details</Text>
          <Row alignmentHorizontal="end" spacing="300" alignmentVertical="center">
            <Row spacing="200" alignmentVertical="center">
              <Text type="b200">Show Counts</Text>
              <Toggle checked={showCounts} onChange={setShowCounts} />
            </Row>
            <ButtonGroup value={selectedPeriodType} onChange={setSelectedPeriodType} type="outline">
              <ButtonOption value="weekly">Weekly View</ButtonOption>
              <ButtonOption value="monthly">Monthly View</ButtonOption>
            </ButtonGroup>
          </Row>
        </Row>
        <DataTable
          showRowDividers={false}
          showColumnDividers={false}
          showStripes={true}
          showBorder={false}
        >
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              <DataTableHeader>Period</DataTableHeader>
              <DataTableHeader>Total Audits</DataTableHeader>
              <DataTableHeader>Incidents with DQO</DataTableHeader>
              <DataTableHeader>Overall DQO Rate</DataTableHeader>
              <DataTableHeader>Incident Location</DataTableHeader>
              <DataTableHeader>Primary Impact</DataTableHeader>
              <DataTableHeader>Detailed Body Part</DataTableHeader>
              <DataTableHeader>Primary Object</DataTableHeader>
              <DataTableHeader>Secondary Object</DataTableHeader>
              <DataTableHeader>Risk Hazard</DataTableHeader>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            <DataTableRow isSectionHeader highlightOnHover={false}>
              <DataTableHeader colSpan={10}>
                {metricsData.year} Yearly Field Summary
              </DataTableHeader>
            </DataTableRow>
            <DataTableRow highlightOnHover={false}>
              <DataTableCell>
                <strong>Yearly Total</strong>
              </DataTableCell>
              <DataTableCell>
                <strong>{formatNumber(totalAudits)}</strong>
              </DataTableCell>
              <DataTableCell>
                <strong>{formatNumber(totalDQO)}</strong>
              </DataTableCell>
              <DataTableCell>
                <strong>{formatPercentage(overallDQORate)}</strong>
              </DataTableCell>
              <DataTableCell>
                <strong>
                  {formatPercentage(
                    fieldTotals["Incident Location"]
                      ? (fieldTotals["Incident Location"].count /
                          fieldTotals["Incident Location"].incidents) *
                          100
                      : 0,
                  )}
                </strong>
              </DataTableCell>
              <DataTableCell>
                <strong>
                  {formatPercentage(
                    fieldTotals["Primary Impact"]
                      ? (fieldTotals["Primary Impact"].count /
                          fieldTotals["Primary Impact"].incidents) *
                          100
                      : 0,
                  )}
                </strong>
              </DataTableCell>
              <DataTableCell>
                <strong>
                  {formatPercentage(
                    fieldTotals["Detailed Body Part"]
                      ? (fieldTotals["Detailed Body Part"].count /
                          fieldTotals["Detailed Body Part"].incidents) *
                          100
                      : 0,
                  )}
                </strong>
              </DataTableCell>
              <DataTableCell>
                <strong>
                  {formatPercentage(
                    fieldTotals["Primary Object"]
                      ? (fieldTotals["Primary Object"].count /
                          fieldTotals["Primary Object"].incidents) *
                          100
                      : 0,
                  )}
                </strong>
              </DataTableCell>
              <DataTableCell>
                <strong>
                  {formatPercentage(
                    fieldTotals["Secondary Object"]
                      ? (fieldTotals["Secondary Object"].count /
                          fieldTotals["Secondary Object"].incidents) *
                          100
                      : 0,
                  )}
                </strong>
              </DataTableCell>
              <DataTableCell>
                <strong>
                  {formatPercentage(
                    fieldTotals["Risk Hazard"]
                      ? (fieldTotals["Risk Hazard"].count / fieldTotals["Risk Hazard"].incidents) *
                          100
                      : 0,
                  )}
                </strong>
              </DataTableCell>
            </DataTableRow>

            <DataTableRow isSectionHeader highlightOnHover={false}>
              <DataTableHeader colSpan={10}>
                {metricsData.period_type.charAt(0).toUpperCase() + metricsData.period_type.slice(1)}{" "}
                Breakdown
              </DataTableHeader>
            </DataTableRow>

            {metricsData.overall_metrics.map((period, index) => {
              const periodFieldMetrics = metricsData.field_metrics.filter(
                (field) => field.period_label === period.period_label,
              );

              const fieldMap = periodFieldMetrics.reduce(
                (acc, field) => {
                  acc[field.field_name] = field;
                  return acc;
                },
                {} as Record<string, FieldPeriodMetricData>,
              );

              return (
                <DataTableRow key={period.period_label} highlightOnHover={true}>
                  <DataTableCell>{period.period_label}</DataTableCell>
                  <DataTableCell>{formatNumber(period.total_incidents)}</DataTableCell>
                  <DataTableCell>{formatNumber(period.override_count)}</DataTableCell>
                  <DataTableCell>{formatPercentage(period.override_rate)}</DataTableCell>
                  <DataTableCell>
                    {fieldMap["Incident Location"]
                      ? showCounts
                        ? `${formatPercentage(fieldMap["Incident Location"].override_rate)} (${formatNumber(fieldMap["Incident Location"].override_count)})`
                        : formatPercentage(fieldMap["Incident Location"].override_rate)
                      : showCounts
                        ? "0.00% (0)"
                        : "0.00%"}
                  </DataTableCell>
                  <DataTableCell>
                    {fieldMap["Primary Impact"]
                      ? showCounts
                        ? `${formatPercentage(fieldMap["Primary Impact"].override_rate)} (${formatNumber(fieldMap["Primary Impact"].override_count)})`
                        : formatPercentage(fieldMap["Primary Impact"].override_rate)
                      : showCounts
                        ? "0.00% (0)"
                        : "0.00%"}
                  </DataTableCell>
                  <DataTableCell>
                    {fieldMap["Detailed Body Part"]
                      ? showCounts
                        ? `${formatPercentage(fieldMap["Detailed Body Part"].override_rate)} (${formatNumber(fieldMap["Detailed Body Part"].override_count)})`
                        : formatPercentage(fieldMap["Detailed Body Part"].override_rate)
                      : showCounts
                        ? "0.00% (0)"
                        : "0.00%"}
                  </DataTableCell>
                  <DataTableCell>
                    {fieldMap["Primary Object"]
                      ? showCounts
                        ? `${formatPercentage(fieldMap["Primary Object"].override_rate)} (${formatNumber(fieldMap["Primary Object"].override_count)})`
                        : formatPercentage(fieldMap["Primary Object"].override_rate)
                      : showCounts
                        ? "0.00% (0)"
                        : "0.00%"}
                  </DataTableCell>
                  <DataTableCell>
                    {fieldMap["Secondary Object"]
                      ? showCounts
                        ? `${formatPercentage(fieldMap["Secondary Object"].override_rate)} (${formatNumber(fieldMap["Secondary Object"].override_count)})`
                        : formatPercentage(fieldMap["Secondary Object"].override_rate)
                      : showCounts
                        ? "0.00% (0)"
                        : "0.00%"}
                  </DataTableCell>
                  <DataTableCell>
                    {fieldMap["Risk Hazard"]
                      ? showCounts
                        ? `${formatPercentage(fieldMap["Risk Hazard"].override_rate)} (${formatNumber(fieldMap["Risk Hazard"].override_count)})`
                        : formatPercentage(fieldMap["Risk Hazard"].override_rate)
                      : showCounts
                        ? "0.00% (0)"
                        : "0.00%"}
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </DataTableBody>
        </DataTable>
      </Column>
    </Column>
  );
};

export default DQOMetrics;
